import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscriber, Subscription } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { BaseApiHandleOptions, BaseApiService } from './api';
import { AddonsIdentifiersEnum, IntentSourceEnum } from '../enums';
import { AccountIntentTopic, IntentTopicAutomationMapping, IntentTopicIds, IntentTopicsDto, CrmAdminConnectedDto, AccountSettingsDto } from '../dto';
import { LocationsService } from './locations.service';
import { User } from 'src/app/core/models/user.model';

@Injectable({
    providedIn: 'root'
})
export class AccountService {
    visitorPage = {
        IsNavigating: false,
        bomboraData: {},
        bomboraResults: [],
        salesintelData: {},
        salesintelResults: [],
        bomboraPage: 0,
        salesintelPage: 0,
        page: 0,
        sortedBy: '',
        sortedByDirection: '',
        dataFilter: '',
        dateFrom: '',
        dateTo: '',
        dateDelta: '',
        selectedDateLabel: '',
        requestBodyForFilter: null
    };

    private addons;
    private addonsSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);
    private readonly topicsSubject: BehaviorSubject<AccountIntentTopic[]> = new BehaviorSubject<AccountIntentTopic[]>(null);
    private readonly intentTopicsMap = new Map<IntentSourceEnum, BehaviorSubject<IntentTopicsDto[]>>();
    private intentTabNumber: number = null;
    private cancelInitSubject: Subject<symbol> = new Subject<symbol>();
    private intentTopicSubscription: Subscription;

    constructor(
        private readonly baseApiService: BaseApiService,
        private readonly locationsService: LocationsService
    ) { }

    setIntentTabNumber(tabNumber: number) {
        this.intentTabNumber = tabNumber;
    }

    getIntentTabNumber() {
        return this.intentTabNumber;
    }

    setIntentTopics(data) {
        this.topicsSubject.next(data);
    }

    getSettings(accountId: number): Observable<any> {
        return this.baseApiService.get(`account/settings/${accountId}`);
    }

    getCrmStatus(accountId: number): Observable<CrmAdminConnectedDto> {
        return this.baseApiService.get(`account/crmStatus/${accountId}`);
    }

    getCrmOwnerMeta(crmOwnerId: number): Observable<any> {
        return this.baseApiService.get(`account/crmOwnerMeta/${crmOwnerId}`);
    }

    getIntentTopics(intentSource?: IntentSourceEnum): Observable<IntentTopicsDto[]> {
        const intentTopics: BehaviorSubject<IntentTopicsDto[]> = this.intentTopicsMap.get(intentSource);
        if (intentTopics?.getValue()?.length) {
            return intentTopics.asObservable();
        }

        return this.baseApiService.get<IntentTopicsDto[]>('service/contactcloud/intent/topics', {
            params: { intentSource }
        }).pipe(tap((topics: IntentTopicsDto[]) => {
            this.intentTopicsMap.set(intentSource, new BehaviorSubject<IntentTopicsDto[]>(topics));
        }));
    }

    getAccountIntentTopics(): Observable<AccountIntentTopic[]> {
        if (!this.topicsSubject.getValue() && !this.intentTopicSubscription) {
            this.intentTopicSubscription = this.baseApiService.get('account/intent/topics', {params: { intentSource: IntentSourceEnum.ALL }})
            .pipe(
                map((response: { topics: AccountIntentTopic[] }) => response?.topics || [])
            )
            .subscribe((topics: AccountIntentTopic[]) => {
                this.setIntentTopics(topics);
            })
        }
        return this.listenToAccountIntentTopics();
    }

    listenToAccountIntentTopics(): Observable<AccountIntentTopic[]> {
        return this.topicsSubject.asObservable();
    }

    postAccountIntentTopics(topics: AccountIntentTopic[], isIntentAutomationsEnabled: boolean, options: BaseApiHandleOptions = {}): Observable<{ response: string }> {
        const intentSaveUrl = isIntentAutomationsEnabled ? 'service/contactcloud/intent/topics' : 'account/intent/topics';
        return this.baseApiService.post<{ response: string }>(intentSaveUrl, { topics }, {
            ...options,
            params: { intentSource: IntentSourceEnum.ALL }
        });
    }

    getIntentAutomationTopicMappingDetails(topicids: IntentTopicIds, options: BaseApiHandleOptions = {}): Observable<IntentTopicAutomationMapping[]> {
        return this.baseApiService.post<{response: IntentTopicAutomationMapping[]}>('service/contactcloud/intent/automation/topic/details', topicids, {
            ...options,
            params: { intentSource: IntentSourceEnum.ALL }
        }).pipe(map(res => res.response));
    }

    getAccountAddons(): Observable<any> {
        return this.baseApiService.get('account/addons');
    }

    init(): Observable<boolean> {
        this.cancelInitSubject.next(Symbol(''));

        return new Observable((subscriber: Subscriber<boolean>) => {
            if (this.addons) {
                subscriber.next(true);
                subscriber.complete();
                return;
            }
            this.getAccountAddons().pipe(takeUntil(this.cancelInitSubject)).subscribe((res: any) => {
                this.addons = res.groups || [];
                this.addonsSubject.next(this.addons);
                subscriber.next(true);
                subscriber.complete();
            }, () => {
                subscriber.next(true);
                subscriber.complete();
            });

            this.locationsService.loadLocations();
            this.getAccountIntentTopics().pipe(take(1)).subscribe();
        })

    }

    listenToAddons(): Observable<any[]> {
        return this.addonsSubject.asObservable();
    }

    hasPlans(identifiers: AddonsIdentifiersEnum[], every: boolean = false): boolean {
        if (every) {
            return identifiers?.every((ident: AddonsIdentifiersEnum) => this.addons?.some((group) => group.identifier === ident));
        }
        return identifiers?.some((ident: AddonsIdentifiersEnum) => this.addons?.some((group) => group.identifier === ident));
    }

    hasPlansAsync(identifiers: AddonsIdentifiersEnum[], every: boolean = false): Observable<boolean> {
        return this.addonsSubject.pipe(
            filter(Boolean),
            map((addons: any[]) => {
                if (every) {
                    return identifiers?.every((ident: AddonsIdentifiersEnum) => addons?.some((group) => group.identifier === ident));
                }
                return identifiers?.some((ident: AddonsIdentifiersEnum) => addons?.some((group) => group.identifier === ident));
            })
        )
    }

    getAddons(): any[] {
        return [...this.addons];
    }

    cleanCachedData(): void {
        this.visitorPage = this.getInitialVisitorPageState();
        this.setIntentTopics(null);
        this.intentTopicSubscription = undefined;
        this.intentTopicsMap.clear();
    }

    isOpsAdmin(accountsettings: AccountSettingsDto, user: User): boolean {
        return accountsettings.ops_admin_ids.includes(user.id);
    }

    isCrmAdmin(accountsettings: AccountSettingsDto, user: User): boolean {
        return (
            accountsettings.salesforce_admin === user.id ||
            accountsettings.hubspot_admin === user.id ||
            accountsettings.dynamics_admin === user.id ||
            accountsettings.zoho_admin === user.id
        );
    }

    userHasAnyCrmOrOpsAdminRole(accountsettings: AccountSettingsDto, user: User): boolean {
        return this.isOpsAdmin(accountsettings, user) || this.isCrmAdmin(accountsettings, user);
    }

    hasAnyCrmAdminConfigured(accountsettings: AccountSettingsDto): boolean {
        return Boolean(
            accountsettings.salesforce_admin ||
            accountsettings.hubspot_admin ||
            accountsettings.dynamics_admin ||
            accountsettings.zoho_admin
        );
    }

    isOpsOrSpecificCrmAdmin(accountsettings: AccountSettingsDto, user: User, crmAdminId: number): boolean {
        return this.isOpsAdmin(accountsettings, user) || crmAdminId === user.id;
    }

    private getInitialVisitorPageState() {
        const initValue = {
            IsNavigating: false,
            bomboraData: {},
            bomboraResults: [],
            salesintelData: {},
            salesintelResults: [],
            bomboraPage: 0,
            salesintelPage: 0,
            page: 0,
            sortedBy: '',
            sortedByDirection: '',
            dataFilter: '',
            dateFrom: '',
            dateTo: '',
            dateDelta: '',
            selectedDateLabel: '',
            requestBodyForFilter: null,
        };
        return { ...initValue };
    }
}
